import { ApiError } from "@nabble/sdk";
import { Alert, Button, Field, Modal, TextInput, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { createOrganization, renameOrganization, type Organization } from "../../../api/adminOrganizations";
import { useAuth } from "../../../auth/AuthContext";
import { errorMessage } from "../../../errors";

interface OrganizationDialogProps {
  /** The organization to rename; null to create one. */
  organization: Organization | null;
  onClose: () => void;
  onSaved: () => void;
}

export function OrganizationDialog({ organization, onClose, onSaved }: OrganizationDialogProps): React.JSX.Element {
  const { show } = useToast();
  const { refreshUser } = useAuth();
  const [name, setName] = useState(organization?.name ?? "");
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    setNameError(null);
    setError(null);
    if (!name.trim()) {
      setNameError("The organization needs a name.");
      return;
    }
    setLoading(true);
    try {
      if (organization) await renameOrganization(organization.id, name.trim());
      else await createOrganization(name.trim());
      show({ title: organization ? "Organization renamed" : "Organization created", text: name.trim(), tone: "success" });
      // The signed-in user's own organization name is shown elsewhere in the suite.
      await refreshUser();
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === "organization_name_taken") setNameError("An organization with that name already exists.");
      else setError(errorMessage(err, "We couldn't save the organization."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      icon="briefcase"
      title={organization ? "Rename organization" : "Create organization"}
      description={
        organization
          ? "People keep belonging to it; only the name changes."
          : "People of an organization see and chat with each other. You choose each person's organization in Users."
      }
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label={organization ? "Save" : "Create organization"} loading={loading} onClick={() => void submit()} />
        </>
      }
    >
      <form className="nb-stack" onSubmit={(e) => void submit(e)} noValidate>
        <Field label="Name" error={nameError ?? undefined}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" placeholder="E.g. Nabble SpA" maxLength={120} autoFocus />
        </Field>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
